import React from "react";
import { Alert, Progress, Space, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import type { DeforestationAssessment, ValidationFinding, ValidationRun } from "../types";
import { DeforestationComparisonPanel } from "./DeforestationComparisonPanel";
import { EudrTermHint } from "./EudrTermHint";
import { SeverityBadge } from "./StatusBadge";
import { MobileDataCards } from "./MobileDataCards";
import { useNarrowViewport } from "../ux/useNarrowViewport";

type Props = {
  validation: ValidationRun | null;
  loading?: boolean;
  /** US-126: structured multi-provider snapshot from case.validation_summary. */
  deforestation?: DeforestationAssessment | null;
};

export function ValidationPanel({ validation, loading, deforestation }: Props) {
  const { t } = useTranslation();
  const isNarrow = useNarrowViewport("md");

  // Backend messages carry coefficient / cutoff / mode; keep them for US-123 demo.
  const dynamicMessageCodes = new Set([
    "DEFORESTATION_STUB",
    "DEFORESTATION_PROVIDER",
    "DEFORESTATION_NO_POLYGON",
    "DEFORESTATION_REGION_HINT",
    "GEO_POLYGON_VALIDATED",
  ]);

  const findingMessage = (finding: ValidationFinding) => {
    if (dynamicMessageCodes.has(finding.code) && finding.message) {
      return finding.message;
    }
    const key = `compliance.findings.${finding.code}.message`;
    const translated = t(key, { defaultValue: "" });
    return translated || finding.message;
  };

  const findingRemediation = (finding: ValidationFinding) => {
    if (!finding.remediation) return finding.remediation;
    const key = `compliance.findings.${finding.code}.remediation`;
    const translated = t(key, { defaultValue: "" });
    return translated || finding.remediation;
  };

  if (!validation) {
    return <Alert type="info" message={t("compliance.validation.runHint")} />;
  }

  return (
    <>
      <div style={{ marginBottom: 16 }} data-testid="validation-risk-block">
        <Space size="small" style={{ marginRight: 16 }}>
          <span>{t("compliance.validation.riskScore")}</span>
          <EudrTermHint term="riskScore" />
        </Space>
        <Progress
          type="dashboard"
          percent={Math.round(validation.risk_score)}
          status={validation.passed ? "success" : "exception"}
          format={(p) => `${p}`}
        />
        <Tag color={validation.passed ? "success" : "error"} style={{ marginLeft: 16 }}>
          {validation.passed ? t("compliance.validation.passed") : t("compliance.validation.blockersPresent")}
        </Tag>
      </div>
      <DeforestationComparisonPanel deforestation={deforestation} compact />
      {isNarrow ? (
        <MobileDataCards
          testId="validation-findings-mobile"
          items={validation.findings}
          rowKey={(row) => row.id}
          title={(row) => row.code}
          fields={(row) => [
            { label: t("common.severity"), value: <SeverityBadge severity={row.severity} /> },
            { label: t("common.message"), value: findingMessage(row) },
            { label: t("common.remediation"), value: findingRemediation(row) || t("common.dash") },
          ]}
        />
      ) : (
        <Table
          size="small"
          loading={loading}
          rowKey="id"
          pagination={false}
          dataSource={validation.findings}
          columns={[
            {
              title: t("common.severity"),
              dataIndex: "severity",
              render: (s: string) => <SeverityBadge severity={s} />,
            },
            { title: t("common.code"), dataIndex: "code", width: 160 },
            {
              title: t("common.message"),
              dataIndex: "message",
              render: (_: string, row: ValidationFinding) => findingMessage(row),
            },
            {
              title: t("common.remediation"),
              dataIndex: "remediation",
              render: (_: string, row: ValidationFinding) => findingRemediation(row),
            },
          ]}
        />
      )}
    </>
  );
}
